// Date Night presentational parts built on the design tokens in tailwind.config.js
import React from 'react';
import { addDays, format, isBefore, isSameDay, parseISO, startOfDay, startOfWeek } from 'date-fns';
import { ArrowLeft, Calendar, Compass, MessageCircle, Moon, Users } from 'lucide-react';
import { OverflowWordmark } from '@/components/Brand';
import type { ScheduledActivity } from '@/types';

export type DateNiteTab = 'browse' | 'explore' | 'calendar' | 'inbox' | 'history';

// Tint + text colour per category area, so moods read the same everywhere
const AREA_TONES: Record<string, string> = {
  her: 'bg-her-tint text-her',
  below: 'bg-her-tint text-her',
  mouth: 'bg-her-tint text-her',
  special: 'bg-action-tint text-action',
  body: 'bg-him-tint text-him',
  face: 'bg-attention-tint text-attention',
  kinky: 'bg-attention-tint text-attention',
};

export const areaTone = (area: string) => AREA_TONES[area] ?? 'bg-raised text-ink-2';

export const AvatarPair: React.FC<{ hisName: string; herName: string; size?: 'sm' | 'lg' }> = ({ hisName, herName, size = 'sm' }) => {
  const dims = size === 'lg' ? 'w-12 h-12 text-base border-[3px]' : 'w-[34px] h-[34px] text-[13px] border-2';
  return (
    <span className="flex shrink-0" aria-hidden="true">
      <span className={`${dims} rounded-full bg-him-tint text-him font-bold flex items-center justify-center border-ground`}>
        {hisName.charAt(0).toUpperCase()}
      </span>
      <span className={`${dims} rounded-full bg-her-tint text-her font-bold flex items-center justify-center border-ground ${size === 'lg' ? '-ml-3.5' : '-ml-2.5'}`}>
        {herName.charAt(0).toUpperCase()}
      </span>
    </span>
  );
};

const TAB_ITEMS: { id: DateNiteTab; label: string; icon: React.ElementType }[] = [
  { id: 'browse', label: 'Tonight', icon: Moon },
  { id: 'explore', label: 'Explore', icon: Compass },
  { id: 'calendar', label: 'Plan', icon: Calendar },
  { id: 'inbox', label: 'Inbox', icon: MessageCircle },
  { id: 'history', label: 'Us', icon: Users },
];

export const DateNiteTabBar: React.FC<{ active: DateNiteTab | null; inboxCount: number; onSelect: (tab: DateNiteTab) => void }> = ({ active, inboxCount, onSelect }) => (
  <nav aria-label="Date Night" className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-[#17151a]/95 backdrop-blur border-t border-[#2a262f] pb-[env(safe-area-inset-bottom)]">
    <div className="max-w-2xl mx-auto grid grid-cols-5 h-16 px-1.5">
      {TAB_ITEMS.map(({ id, label, icon: Icon }) => {
        const isActive = active === id;
        return (
          <button
            key={id}
            onClick={() => onSelect(id)}
            aria-current={isActive ? 'page' : undefined}
            className={`relative flex flex-col items-center justify-center gap-1 text-[11px] ${isActive ? 'text-action font-bold' : 'text-ink-muted font-semibold hover:text-ink-2'}`}
          >
            <Icon className="w-[22px] h-[22px]" />
            {label}
            {id === 'inbox' && inboxCount > 0 && (
              <span className="absolute top-1.5 left-1/2 ml-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-her text-ground text-[11px] font-bold flex items-center justify-center">
                {inboxCount > 9 ? '9+' : inboxCount}
              </span>
            )}
          </button>
        );
      })}
    </div>
  </nav>
);

// Desktop: the same tabs as a left sidebar
export const DateNiteSideNav: React.FC<{
  active: DateNiteTab | null;
  inboxCount: number;
  onSelect: (tab: DateNiteTab) => void;
  onHome: () => void;
  hisName: string;
  herName: string;
}> = ({ active, inboxCount, onSelect, onHome, hisName, herName }) => (
  <nav aria-label="Date Night" className="hidden lg:flex fixed inset-y-0 left-0 z-40 w-64 flex-col border-r border-line bg-[#17151a] px-4 py-6">
    <button onClick={onHome} className="px-2 text-left" aria-label="Overflow home"><OverflowWordmark size="md" /></button>
    <div className="mt-8 flex items-center gap-3 px-2">
      <AvatarPair hisName={hisName} herName={herName} />
      <span className="text-[14px] font-semibold truncate">{hisName} & {herName}</span>
    </div>
    <div className="mt-6 space-y-1">
      {TAB_ITEMS.map(({ id, label, icon: Icon }) => {
        const isActive = active === id;
        return (
          <button
            key={id}
            onClick={() => onSelect(id)}
            aria-current={isActive ? 'page' : undefined}
            className={`w-full h-11 px-3 rounded-xl flex items-center gap-3 text-[15px] ${isActive ? 'bg-action-tint text-action font-bold' : 'text-ink-2 font-semibold hover:bg-surface hover:text-ink'}`}
          >
            <Icon className="w-5 h-5" />
            <span className="flex-1 text-left">{label}</span>
            {id === 'inbox' && inboxCount > 0 && (
              <span className="min-w-[22px] h-[22px] px-1.5 rounded-full bg-her text-ground text-[12px] font-bold flex items-center justify-center">{inboxCount > 9 ? '9+' : inboxCount}</span>
            )}
          </button>
        );
      })}
    </div>
    <button onClick={onHome} className="mt-auto h-11 px-3 rounded-xl flex items-center gap-3 text-[14px] font-semibold text-ink-muted hover:text-ink hover:bg-surface">
      <ArrowLeft className="w-4 h-4" /> Back to home
    </button>
  </nav>
);

// Mon–Sun of the current week with planned (lime) and done (pink) markers
export const WeekStrip: React.FC<{ activities: ScheduledActivity[]; onSelectDay: (day: Date) => void }> = ({ activities, onSelectDay }) => {
  const today = new Date();
  const monday = startOfWeek(today, { weekStartsOn: 1 });
  const days = Array.from({ length: 7 }, (_, i) => addDays(monday, i));
  const sunday = days[6]!;
  return (
    <div>
      <div className="flex justify-between text-[13px] text-ink-muted mb-2">
        <span>This week</span>
        <span>{format(monday, 'd MMM')} – {format(sunday, 'd MMM')}</span>
      </div>
      <div className="grid grid-cols-7 gap-1">
        {days.map(day => {
          const dayActivities = activities.filter(a => isSameDay(parseISO(a.scheduledDate), day));
          const planned = dayActivities.some(a => !a.completedAt);
          const done = dayActivities.some(a => a.completedAt);
          const isToday = isSameDay(day, today);
          const isPast = isBefore(day, startOfDay(today));
          const label = `${format(day, 'EEEE d MMMM')}${planned ? ', date planned' : ''}${done ? ', date done' : ''}`;
          return (
            <button
              key={day.toISOString()}
              onClick={() => onSelectDay(day)}
              disabled={isPast}
              aria-label={label}
              className="flex flex-col items-center gap-1.5 py-1 rounded-xl hover:bg-surface disabled:hover:bg-transparent disabled:opacity-60"
            >
              <span className={`text-xs ${isToday ? 'text-action font-bold' : 'text-ink-muted'}`}>{format(day, 'EEEEE')}</span>
              <span className={`w-10 h-10 rounded-full flex items-center justify-center text-[15px] ${isToday ? 'bg-action text-ground font-bold' : planned ? 'border border-line-strong text-ink' : 'text-ink-2'}`}>
                {format(day, 'd')}
              </span>
              <span className={`w-1.5 h-1.5 rounded-full ${planned ? 'bg-action' : done ? 'bg-her' : ''}`} />
            </button>
          );
        })}
      </div>
    </div>
  );
};
